import React from "react";
//Limitless imports
import { log as pilog } from "../../utils";
import { processRestrictedData, processLocaleData, processAiServiceProvidersData } from "./AppConfigHelper";
import { fetchMultipleConfigItems } from "../../apis/ConfigAPI";
export const CONFIG_LOCALE = {
  name: "uiSupportedLocaleList",
  dataProcessor: processLocaleData,
  stateKey: "localeConfig"
};
export const CONFIG_TIMEZONES = {
  name: "uiSupportedTimeZone",
  stateKey: "timeZones"
};
export const CONFIG_DIALCODES = {
  name: "uiSupportedDialCodes",
  stateKey: "dialCodes"
};
export const CONFIG_APP_VERSIONS = {
  name: "uiAppVersions",
  stateKey: "appVersions"
};
export const CONFIG_WIPE_DATA_DELAY = {
  name: "wipeDataDelay",
  stateKey: "wipeDataDelay",
  restricted: true,
  dataProcessor: processRestrictedData
};
export const CONFIG_FEATURE_SWITCHES = {
  name: "uiFeatureSwitches",
  stateKey: "featureSwitches"
};
export const CONFIG_INCENTIVE_FEE_TYPES = {
  name: "uiIncentiveFeeTypes",
  stateKey: "incentiveFeeTypes"
};
export const CONFIG_FEE_ADJUSTMENT = {
  name: "feeAdjustment",
  stateKey: "feeAdjustment"
};
export const CONFIG_DISTRIBUTION_GROUPS = {
  name: "uiAvailablityGroupList",
  stateKey: "availablityGroupList"
};
export const CONFIG_CURRENCY_CONFIG = {
  name: "currencyConfig",
  stateKey: "currencyConfig"
};
export const CONFIG_PUBLIC_KB_LIST = {
  name: "publicKbList",
  stateKey: "publicKbList",
  restricted: true,
  dataProcessor: processRestrictedData
};
export const CONFIG_SECURE_DATA = {
  name: "custSecureDataList",
  stateKey: "custSecureDataList"
};
export const CONFIG_EXPERT_SUPPORT_DETAILS = {
  name: "expertSupportDetails",
  stateKey: "expertSupportDetails"
};
export const CONFIG_GET_TASKS_STICKY_THRESHOLD = {
  name: "tasksStickyThreshold",
  stateKey: "tasksStickyThreshold"
};
export const CONFIG_GET_DASHBOARD_THRESHOLD = {
  name: "dashboardMsgCount",
  stateKey: "dashboardMsgCount"
};
export const CONFIG_AI_SERVICE_PROVIDERS = {
  name: "aiServiceProviders",
  stateKey: "aiServiceProviders",
  restricted: true,
  dataProcessor: processAiServiceProvidersData
};

//shared config between EMA & EWA
export const EXPERT_CONFIG = [CONFIG_LOCALE, CONFIG_DIALCODES, CONFIG_FEATURE_SWITCHES, CONFIG_CURRENCY_CONFIG, CONFIG_EXPERT_SUPPORT_DETAILS, CONFIG_GET_TASKS_STICKY_THRESHOLD, CONFIG_GET_DASHBOARD_THRESHOLD];
const INITIAL_STATE = {
  tasksStickyThreshold: 2,
  dashboardMsgCount: 20,
  localeConfig: {
    countryList: [{
      currencyDescription: "UK Pound",
      default: true,
      countryDescription: "United Kingdom",
      currencySign: "£",
      dateFormat: "dd-mm-YYYY",
      countryCode: "GB",
      languageDescription: "English",
      languageCode: "en",
      currencyCode: "GBP",
      locale: "en-GB"
    }],
    defaultLocale: {}
  },
  timeZones: [{
    timeZone: "UTC",
    timeZoneDescription: "UTC (UTC)"
  }],
  dialCodes: [],
  appVersions: [],
  wipeDataDelay: 0,
  featureSwitches: {
    enforceCompleteProfile: false,
    pendingMessageDetail: false,
    expertLLMSupport: false,
    webSocketDebug: true
  },
  incentiveFeeTypes: [],
  feeAdjustment: [],
  availablityGroupList: [],
  currencyConfig: [],
  custSecureDataList: [],
  expertSupportDetails: {
    publicSupport: {
      apiKey: "",
      tenantName: "",
      groupName: "",
      locale: ""
    },
    privateSupport: {
      apiKey: "",
      tenantName: "",
      groupName: "",
      locale: ""
    },
    faqKnowledgeBase: {
      tenantName: "",
      groupName: ""
    },
    llmLandingPageUrl: ""
  }
};
const getInitialState = (configItems, initialConfig) => {
  let initialState = {
    loading: true
  };
  configItems.map(configItem => {
    initialState = {
      ...initialState,
      [configItem.stateKey]: INITIAL_STATE[configItem.stateKey]
    };
  });
  if (initialConfig) {
    initialState = {
      ...initialState,
      ...initialConfig
    };
  }
  return initialState;
};

/** convenience wrapper around the flo-conifg currently managed in Redux */
export const useConfig = (configItems, initialConfig) => {
  var _config$localeConfig$, _config$localeConfig;
  const log = pilog.getLogger("useConfig");
  const [config, setConfig] = React.useState(getInitialState(configItems, initialConfig));
  const countryList = (_config$localeConfig$ = (_config$localeConfig = config.localeConfig) === null || _config$localeConfig === void 0 ? void 0 : _config$localeConfig.countryList) !== null && _config$localeConfig$ !== void 0 ? _config$localeConfig$ : INITIAL_STATE.localeConfig.countryList;
  log.debug({
    config
  });
  React.useEffect(() => {
    loadPublicAppConfig();
  }, []);
  const loadPublicAppConfig = () => {
    const publicItems = configItems.filter(ci => !ci.restricted);
    fetchConfig(publicItems);
  };

  //we pass the headers which contain Authentication tokens here.
  //not required by customer apps
  const loadRestrictedAppConfig = headers => {
    const restrictedItems = configItems.filter(ci => ci.restricted);
    if (restrictedItems.length) {
      fetchConfig(restrictedItems, headers);
    }
  };
  const fetchConfig = (items, restrictedHeaders) => {
    fetchMultipleConfigItems(items, restrictedHeaders, data => {
      if (data) {
        setConfig({
          ...config,
          ...data,
          loading: false
        });
      }
    });
  };
  const updateConfig = data => {
    setConfig({
      ...config,
      ...data
    });
  };
  const findLocaleFromCountryLanguage = locale => {
    var _config$localeConfig2;
    const country = (_config$localeConfig2 = config.localeConfig) === null || _config$localeConfig2 === void 0 ? void 0 : _config$localeConfig2.countryList.find(item => item.locale === locale);
    return country ? {
      ...country,
      value: country.locale
    } : null;
  };
  const getCurrencySigns = () => {
    return [...new Set(countryList.map(country => {
      return country.currencySign;
    }))];
  };
  const getFeatureSwitches = () => {
    return config.featureSwitches;
  };
  const getFeeAdjustmentSystemConfig = () => {
    return config.feeAdjustment;
  };
  const getTranxApprovalThresholdConfig = () => {
    return config.currencyConfig;
  };
  const getIncentiveFeeTypes = () => {
    return config.incentiveFeeTypes;
  };
  const getAvailablityGroupList = () => {
    return config.availablityGroupList;
  };
  const getCustSecureDataList = () => {
    return config.custSecureDataList;
  };
  const getTasksStickyThreshold = () => {
    return config.tasksStickyThreshold;
  };
  const getDashboardMsgCount = () => {
    return config.dashboardMsgCount;
  };
  const getMinWithdrawalAmount = (currencyCode = "GBP") => {
    const MIN_WITHDRAWAL_AMT = 5;
    let min = 0;
    const thresholdConfig = getTranxApprovalThresholdConfig();
    if (thresholdConfig.length > 0) {
      var _thresholdConfig$find, _thresholdConfig$find2;
      min = (_thresholdConfig$find = thresholdConfig.find(item => item.code === currencyCode)) === null || _thresholdConfig$find === void 0 ? void 0 : (_thresholdConfig$find2 = _thresholdConfig$find.withdrawal) === null || _thresholdConfig$find2 === void 0 ? void 0 : _thresholdConfig$find2.valueThreshold;
    }
    return min >= 0 ? min : MIN_WITHDRAWAL_AMT;
  };
  const getLanguageFromLanguageCode = languageCode => {
    var _config$localeConfig3;
    let language = null;
    (_config$localeConfig3 = config.localeConfig) === null || _config$localeConfig3 === void 0 ? void 0 : _config$localeConfig3.countryList.forEach(item => {
      if (item.languageCode === languageCode) {
        language = item.languageDescription;
      }
    });
    return language;
  };
  const getExpertSupportDetails = () => {
    var _config$expertSupport;
    return (_config$expertSupport = config.expertSupportDetails) !== null && _config$expertSupport !== void 0 ? _config$expertSupport : INITIAL_STATE.expertSupportDetails;
  };
  const getContactUsUrl = navigationUrl => {
    const {
      expertLLMSupport
    } = getFeatureSwitches();
    return expertLLMSupport ? navigationUrl ? navigationUrl : process.env.REACT_APP_PUBLIC_CONTACT_US_URL : "https://www.limitlesstech.com/expert-support";
  };
  return {
    config,
    countryList,
    getFeatureSwitches,
    getFeeAdjustmentSystemConfig,
    getTranxApprovalThresholdConfig,
    findLocaleFromCountryLanguage,
    loadAppConfig: loadPublicAppConfig,
    loadRestrictedAppConfig,
    getCurrencySigns,
    getIncentiveFeeTypes,
    getAvailablityGroupList,
    getMinWithdrawalAmount,
    getCustSecureDataList,
    updateConfig,
    getLanguageFromLanguageCode,
    getExpertSupportDetails,
    getContactUsUrl,
    getTasksStickyThreshold,
    getDashboardMsgCount
  };
};